/**
 * Bayram mavzulari — katta bayramlarda ilova foni o'zgaradi.
 *
 * QOIDA: mavzu FAQAT BEZAKNI o'zgartiradi — fon gradienti, ustidagi
 * naqsh qatlami (`holiday-decor.tsx`) va DEKORATIV urg'u rangi
 * (`--accent`: ikonka pallalari). MATN ranglari va kontrast TEGILMAYDI
 * (TZ §10.7): `--fg`, `--fg-muted`, `--gold-text`, `--on-gold` o'zgarmas.
 * Sabab: kontrast nisbatlari o'lchangan va ular bayramga qarab
 * buzilmasligi kerak. `--accent` faqat ikonkaga tegadi — unga 3:1
 * kifoya va har bir bayram uchun alohida o'lchangan (styles.css).
 *
 * OYNA: bayramdan `PADDING_DAYS` kun oldin boshlanadi va shuncha kun
 * keyin tugaydi. Ya'ni 3 kunlik Navro'z 23 kunlik mavzuga aylanadi.
 * Jami yiliga ~118 kun (yilning uchdan biri) bayram fonida o'tadi.
 *
 * SANALAR IKKI XIL:
 *   * qat'iy — har yili bir xil kun (Navro'z, Mustaqillik, Yangi yil)
 *   * ko'chuvchi — oy taqvimiga bog'liq (Ramazon va Qurbon hayit).
 *     Ular hisoblanmaydi, JADVALDAN olinadi: hisoblash formulalari
 *     mamlakatlar orasida farq qiladi va bir kunga adashish bayramni
 *     noto'g'ri kunda ko'rsatadi. Jadval tugaganda bayram shunchaki
 *     ko'rsatilmaydi — bu xato emas, uni yangilash kerak degani.
 *
 * TIL (MultiLang_TZ §8.8). Har bir bayram qaysi til lentasida
 * ko'rinishini `langs` belgilaydi; `undefined` — hamma til. Rus
 * kanalidan kelgan qozog'istonlik yoki belarusiyalik foydalanuvchiga
 * O'zbekiston Mustaqillik kuni fonini ko'rsatish ma'nosiz.
 *
 * Tabrik MATNI bu yerda YO'Q — u lug'atda (`holiday.<key>.greeting`,
 * `.note`). Bu yerda faqat sana, mavzu va til — ya'ni ma'lumot.
 */

export interface Holiday {
  /** Lug'at kaliti ham shundan: `holiday.<key>.greeting` / `.note`. */
  key: string;
  /** Ilova ildiziga qo'yiladigan `data-holiday` qiymati. */
  theme: string;
  /** Qaysi tillarda ko'rinadi. `undefined` — hamma tilda. */
  langs?: readonly string[];
}

/** Bayramdan necha kun oldin va keyin mavzu turadi. */
export const PADDING_DAYS = 10;

// O'zbekiston auditoriyasiga xos bayramlar — faqat o'zbek lentasida.
// Boshqa tilga ochish uchun `langs` ga kod qo'shish kifoya.
const UZ_ONLY = ["uz"] as const;

const NAVRUZ: Holiday = { key: "navruz", theme: "navruz", langs: UZ_ONLY };
const INDEPENDENCE: Holiday = {
  key: "independence", theme: "independence", langs: UZ_ONLY,
};
// Yangi yil — hamma auditoriyada bayram, `langs` yo'q.
const NEW_YEAR: Holiday = { key: "new_year", theme: "winter" };
// Ramazon va Qurbon BIR XIL mavzuni (`eid`) baham ko'radi — bezak
// ikkalasiga ham to'g'ri keladi. Tabrik matni esa har biriga alohida.
const RAMADAN: Holiday = { key: "ramadan", theme: "eid", langs: UZ_ONLY };
const QURBON: Holiday = { key: "qurbon", theme: "eid", langs: UZ_ONLY };

/** Bayram shu tilda ko'rinadimi. */
function shownIn(holiday: Holiday, lang: string): boolean {
  return !holiday.langs || holiday.langs.includes(lang);
}

/**
 * Ko'chuvchi bayramlar. O'zbekistondagi rasmiy dam olish kunlariga
 * qarab yangilanadi. Sanalar bayramning O'ZI — kengaytirish
 * `PADDING_DAYS` orqali avtomatik.
 */
const LUNAR: { holiday: Holiday; from: string; to: string }[] = [
  { holiday: RAMADAN, from: "2026-03-19", to: "2026-03-21" },
  { holiday: QURBON, from: "2026-05-26", to: "2026-05-28" },
  { holiday: RAMADAN, from: "2027-03-09", to: "2027-03-11" },
  { holiday: QURBON, from: "2027-05-16", to: "2027-05-18" },
];

/**
 * Qat'iy bayramlar: [oy, kun] dan [oy, kun] gacha.
 *
 * Yangi yil YIL CHEGARASIDAN O'TADI (25-dekabr -> 2-yanvar). Shuning
 * uchun oddiy "oy va kun" solishtiruvi ishlamaydi — quyida haqiqiy
 * sanalar quriladi.
 */
const FIXED: { holiday: Holiday; from: [number, number]; to: [number, number] }[] = [
  { holiday: NEW_YEAR, from: [12, 25], to: [1, 2] },
  { holiday: NAVRUZ, from: [3, 20], to: [3, 22] },
  { holiday: INDEPENDENCE, from: [9, 1], to: [9, 2] },
];

/** Kun boshiga keltirilgan mahalliy sana — solishtirish uchun. */
function atMidnight(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function addDays(date: Date, days: number): Date {
  const out = new Date(date);
  out.setDate(out.getDate() + days);
  return out;
}

function parseIso(iso: string): Date {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
}

interface Window {
  holiday: Holiday;
  from: Date;
  to: Date;
}

/** Berilgan yil uchun qat'iy bayram oynalari (chegaradan o'tishi mumkin). */
function fixedWindows(year: number): Window[] {
  return FIXED.map(({ holiday, from, to }) => {
    const start = new Date(year, from[0] - 1, from[1]);
    let end = new Date(year, to[0] - 1, to[1]);
    // Tugash boshlanishdan oldin bo'lsa — keyingi yilga o'tgan
    if (end < start) end = new Date(year + 1, to[0] - 1, to[1]);
    return { holiday, from: start, to: end };
  });
}

/**
 * Shu sanada, shu tilda bayram bormi (oldin/keyin `PADDING_DAYS` bilan).
 * Yo'q bo'lsa `null` — ilova odatdagi brend fonida qoladi.
 *
 * Boshqa tildagi bayram O'TKAZIB YUBORILADI va qidiruv davom etadi:
 * 2026-mart oxirida o'zbek foydalanuvchisi Ramazonni ko'radi, rus
 * foydalanuvchisi esa hech narsa ko'rmaydi — Navro'zni ham emas,
 * chunki u ham faqat o'zbek lentasida.
 */
export function holidayOn(date: Date = new Date(), lang = "uz"): Holiday | null {
  const today = atMidnight(date);

  const inside = ({ from, to }: Window): boolean =>
    today >= addDays(from, -PADDING_DAYS) && today <= addDays(to, PADDING_DAYS);

  // KO'CHUVCHI BAYRAM USTUN. Ular qat'iy sana bilan ustma-ust tushadi:
  // 2026-yilda Ramazon hayit 19-21 mart, Navro'z esa 20-22 mart —
  // kengaytirilgandan keyin oynalar deyarli to'liq qoplashadi.
  // Ko'chuvchisi tanlangan, chunki uning sanasi har yili siljiydi va
  // shu yili aynan qachon ekani ko'proq e'tibor talab qiladi.
  // Tartibni almashtirish uchun quyidagi ikki blokning joyini almashtiring.
  for (const { holiday, from, to } of LUNAR) {
    if (!shownIn(holiday, lang)) continue;
    if (inside({ holiday, from: parseIso(from), to: parseIso(to) })) {
      return holiday;
    }
  }

  // Qo'shni yillar ham tekshiriladi: dekabrdagi oyna yanvarga o'tadi,
  // yanvardagi kun esa o'tgan yilning oynasiga tegishli bo'lishi mumkin.
  const year = today.getFullYear();
  for (const y of [year - 1, year, year + 1]) {
    for (const window of fixedWindows(y)) {
      if (shownIn(window.holiday, lang) && inside(window)) return window.holiday;
    }
  }
  return null;
}

/** Barcha bayramlar — dev majburlash va testlar uchun. */
export const ALL = [NAVRUZ, INDEPENDENCE, NEW_YEAR, RAMADAN, QURBON];

/**
 * FAQAT DEV: `?holiday=eid` bilan mavzuni majburlash.
 *
 * Bayram dizaynini avgustda ham ko'rish kerak bo'ladi — tizim
 * soatini o'zgartirish yoki jadvalni vaqtincha buzish o'rniga
 * shu yo'l bor. `key` (ramadan/qurbon) ham, `theme` (eid) ham
 * qabul qilinadi.
 *
 * `import.meta.env.DEV` SHART: Vite uni qurish paytida `false` ga
 * almashtiradi va butun shox bundldan chiqib ketadi. Ya'ni prodda
 * bu parametr umuman mavjud emas — kimdir havolani ulashsa ham
 * bayram fonini majburlab qo'ya olmaydi.
 */
function forcedHoliday(): Holiday | null {
  if (!import.meta.env?.DEV) return null;
  const want = new URLSearchParams(window.location.search).get("holiday");
  if (!want) return null;
  return ALL.find((h) => h.key === want || h.theme === want) ?? null;
}

/**
 * Mavzuni hujjat ildiziga qo'yadi. `App` IKKI marta chaqiradi:
 * birinchi kadrda oxirgi ma'lum til bilan, `/api/me` kelgach haqiqiy
 * til bilan. Bayram bo'lmasa atribut olib tashlanadi.
 */
export function applyHolidayTheme(date: Date = new Date(),
                                  lang = "uz"): Holiday | null {
  const holiday = forcedHoliday() ?? holidayOn(date, lang);
  const root = document.documentElement;
  if (holiday) root.setAttribute("data-holiday", holiday.theme);
  else root.removeAttribute("data-holiday");
  return holiday;
}
